import {useState} from 'react';
import {
  setFileUploadProgress,
  setMediaModalVisible,
  setUploadInProgress,
} from '../store/slices/upload';
import {
  generateAudioSpectrogram,
  generateVideoForImage,
  getFileOpfs,
  getPreviewFromVideo,
  shareFileToWT,
  writeFileOpfs,
} from '../api/media';
import {useMutation, useQueryClient} from 'react-query';
import {domain, cdndomain, sendChatMessage, findBy} from '../api';
import {
  EditorLayer,
  EditorProject,
  EncoderProject,
  FFMpegOutputType,
  FFMpegPreset,
  Folder,
  FolderType,
  Media,
  Product,
} from '../types';

import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useNavigation} from '@react-navigation/native';
import uuid from 'react-native-uuid';
import {getById, writeItem} from '../api';
import axios from 'axios';
import {signData} from '../api/ssl';
import {getPremiumKey} from '../api/billing';
import {getUserStorageAddress} from '../api/exchange';
import {manipulateAsync, SaveFormat} from 'expo-image-manipulator';
import {Platform} from 'react-native';
import {faker} from '@faker-js/faker';
import {defaultVideoDuration} from '../api/encoder/util/genVideoFromImage';

const preset = FFMpegPreset.superfast;
const output = FFMpegOutputType.mp4;
const compressionLevel = 20;

export interface FileUploadProps {
  userId?: string;
  folderType: FolderType;
  hideMedialModal: () => void;
  folderId: string;
  routeName: string;
  projectId: string;
  isEncoderProject: boolean;
}

const qualityToSize = {
  HD: {width: 1280, height: 720},
  SD: {width: 854, height: 480},
  LOW: {width: 640, height: 360},
};

const createEditorProject = () => {};
export function useConfirmFile(props: FileUploadProps) {
  //FILE UPLOAD
  const {
    userId,
    folderType,
    folderId,
    routeName,
    projectId,
    isEncoderProject,
  } = props;

  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const nav = useNavigation();
  const uploadState = useAppSelector(state => state.upload);
  const dispatch = useAppDispatch();
  const mediaModalVisible = uploadState.modalVisible;
  const pickerFile = uploadState.file;
  const pickerFileMeta = uploadState.media;
  const uploadToCloud = uploadState.uploadToCloud;
  const shareToWt = uploadState.shareToWT;
  const encryptionEnabled = uploadState.encryption;
  const fileUploadProgress = uploadState.fileUploadProgress;
  const isUploadInProgress = uploadState.isUploadInProgress;
  const uuidFilename = `${pickerFileMeta?.id}-${pickerFileMeta?.filename}`;
  const writeItemMutation = useMutation(
    (item: Media | Folder | EditorProject | EditorLayer) =>
      writeItem(item),
  );

  const fileType =
    pickerFile?.type ||
    pickerFile?.mimeType ||
    pickerFileMeta?.fileType;

  const isEditorProject =
    folderType === FolderType.editor && routeName !== 'EditorProject';
  const [videoMeta, setVideoMeta] = useState({
    quality: 'HD' as keyof typeof qualityToSize,
    aspectRatio: '16/9',
    editorTitle: isEditorProject
      ? 'Project ' + faker.animal.fish()
      : undefined,
    width: pickerFile?.width,
    height: pickerFile?.height,
    duration: defaultVideoDuration,
  });

  const uploadFileMutation = useMutation(async () => {
    const userContractAddress = await getUserStorageAddress();
    const key = await getPremiumKey();
    const signature = await signData(
      key.keyPair.privateKey as JsonWebKey,
      userContractAddress,
    );
    const res = await axios.request({
      url: `${domain}api/v1/backend/files/${uuidFilename}?userContractAddress=${userContractAddress}&signature=${signature}`,
      method: 'PUT',
      data: pickerFile,
      onUploadProgress: progress =>
        dispatch(setFileUploadProgress(progress)),
    });
    return res.data as {result: string};
  });

  const writeItemToLocalStoreMutation = useMutation(meta =>
    writeFileOpfs({...meta, ...videoMeta}, pickerFile!),
  );
  const isImage = fileType.search('image') > -1;
  const isAudio = fileType.search('audio') > -1;
  const isVideo = fileType.search('video') > -1;
  const isImageType = isImage;
  const confirmFileMutation = useMutation(async () => {
    let peerShared = shareToWt;
    if (pickerFileMeta?.extractor === 'wt') {
      peerShared = true;
    }
    dispatch(setUploadInProgress(true));
    setLoading(true);
    try {
      const meta: Media = JSON.parse(JSON.stringify(pickerFileMeta!));
      console.info(
        'isImage=' + isImage,
        'isAudio=' + isAudio,
        'isVideo=' + isVideo,
      );
      const originalUrl = `${pickerFileMeta.url?.replace(
        'file://',
        '',
      )}`;
      let {width, height, aspectRatio} = videoMeta;
      const end = (videoMeta?.duration || defaultVideoDuration) - 1;
      meta.isDownloaded = true;
      let folder: Folder | undefined;
      try {
        folder = await getById(meta.folderId);
        if (!folder) {
          folder = (await getById(meta.folderId, folderId)) as Folder;
        }
        if (folder?.mnemonic) {
          peerShared = true;
        }
      } catch (err) {
        console.error('err', err);
      }
      const editorProjectId = uuid.v4().toString();
      let editor: EditorProject | undefined = undefined;
      if (isEditorProject) {
        console.info(
          'creating a new editor project from file',
          videoMeta,
        );
        if (!videoMeta.quality)
          videoMeta.quality = 'HD' as keyof typeof qualityToSize;
        const librarySizes = qualityToSize[videoMeta.quality];
        const isPortrait = videoMeta.aspectRatio === '9/16';
        const projectWidth = isPortrait
          ? librarySizes.height
          : librarySizes.width;
        const projectHeight = isPortrait
          ? librarySizes.width
          : librarySizes.height;
        width = projectWidth;
        height = projectHeight;
        editor = {
          id: editorProjectId,
          title: videoMeta.editorTitle || meta.title,
          userId,
          isEditorProject: true,
          folderType,
          folderId,
          orderId: 1,
          isFolder: false,
          editor: {
            length: 1 * 60 * 1000,
            width: projectWidth,
            height: projectHeight,
            aspectRatio,
            preset,
            output,
            lastUpdated: new Date().valueOf(),
            compressionLevel,
            code: [],
          },
          mnemonic: folder?.mnemonic,
          date: new Date().valueOf(),
        };
        await writeItemMutation.mutateAsync(editor);
        const layer: EditorLayer = {
          id: uuid.v4().toString(),
          title: 'Layer 1',
          folderId: editor.id,
          userId,
          isEditorLayer: true,
          orderId: 1,
          folderType,
          isFolder: false,
          date: new Date().valueOf(),
          editor: {
            videoDisabled: isAudio ? true : false,
            muted: isImage ? true : false,
          },
          mnemonic: folder?.mnemonic,
        };
        await writeItemMutation.mutateAsync(layer);
        meta.folderId = layer.id;
        meta.isMediaNew = isEncoderProject;
        meta.isEncoderProject = isEncoderProject;
        meta.encoding = {
          end,
          width,
          height,
          preset,
          output,
          compressionLevel,
        };
      }
      if (folderType === FolderType.editor) {
        // const project = project

        (meta as EncoderProject).encoding = {
          end,
          width,
          height,
          preset,
          output,
          compressionLevel,
        };
      }
      if (folderType === FolderType.shop) {
        (meta as Product).isProduct = true;
        (meta as Product).price = 1;
      }
      if (peerShared) {
        meta.extractor = 'wt';
      }
      if (!meta.extractor) meta.extractor = 'local';
      await writeItemMutation.mutateAsync({
        ...{...meta, url: undefined},
        ...videoMeta,
      });
      await writeItemToLocalStoreMutation.mutateAsync(meta);
      delete meta.url;
      let didDoImageToVideo = false;
      try {
        if (folderType === FolderType.editor && isImage) {
          const editorProject: EditorProject = await getById(
            projectId,
          );
          const videoProcessing = await generateVideoForImage({
            ...meta,
            ...videoMeta,
            ...{
              width: editorProject?.editor.width,
              height: editorProject?.editor.height,
            },
          } as EncoderProject);
          const newUrl =
            Platform.OS === 'web'
              ? ((await getFileOpfs(meta)) + '.mp4').replace(
                  'file://',
                  '',
                )
              : videoProcessing;
          const ntype = 'video/mp4';
          const nmedia = {
            ...meta,
            ...videoMeta,
            url: newUrl,
            filename: meta.filename + '.mp4',
            fileType: ntype,
            type: ntype,
          };
          console.info('updating image url after processing', nmedia);
          await writeItemMutation.mutateAsync(nmedia);
          didDoImageToVideo = true;
        }
      } catch (err) {
        console.error('process image to video err', err);
      }

      if (isAudio || isVideo) {
        await generateAudioSpectrogram(
          {
            ...meta,
            ...videoMeta,
          } as Media,
          'big',
        );
        await generateAudioSpectrogram(
          {
            ...meta,
            ...videoMeta,
          } as Media,
          'small',
        );
      }

      if (uploadToCloud) {
        dispatch(setUploadInProgress(true));
        dispatch(setMediaModalVisible(false));
        const uploadRes = await uploadFileMutation.mutateAsync();
        const fileUrl = cdndomain + uploadRes.result;
        meta.webpage_url = fileUrl;
        meta.hasCloud = true;
        meta.extractor = 'cloud';
        meta.url = fileUrl;
        console.info('after upload File Mutation', meta);
        await writeItemMutation.mutateAsync({...meta, ...videoMeta});
      }
      let image: string | undefined;
      try {
        if (didDoImageToVideo) throw 'already_did';
        if (isVideo) {
          image = await getPreviewFromVideo({...meta, ...videoMeta});
        }

        if (isImageType) {
          const manipulated = await manipulateAsync(
            originalUrl,
            [{resize: {width: 640}}],
            {compress: 0.1, format: SaveFormat.JPEG, base64: true},
          );
          image =
            Platform.OS === 'web'
              ? manipulated.uri
              : 'data:image/jpeg;base64,' + manipulated.base64;

          if (image && !isEditorProject) {
            await writeItem({...meta, ...videoMeta, image});
          }
        }
      } catch (err) {
        console.error('preview generation error', err);
      }
      let peerPromise;
      try {
        if (peerShared)
          peerPromise = await shareFileToWT(meta.id, meta.folderId);
      } catch (err) {
        console.error('share to wt error', err);
      }
      if (meta.isChatMedia) {
        const mediaNew = (await getById(meta.id, folderId)) as Media;
        await sendChatMessage(
          meta.folderId,
          '[media]',
          meta.mnemonic,
          {
            image,
            media: {...mediaNew, ...videoMeta},
          },
        );
      }
      // await peerPromise;
      queryClient.refetchQueries({
        queryKey: ['folderContent_' + meta.folderId],
      });
      queryClient.refetchQueries({
        queryKey: ['folderContent_' + folderId],
      });
      queryClient.refetchQueries({
        queryKey: ['projectLayers', projectId],
      });
      queryClient.refetchQueries({
        queryKey: ['encodedMedia', projectId],
      });
      dispatch(setUploadInProgress(false));
      dispatch(setMediaModalVisible(false));
      if (!folderId) {
        nav.navigate('Folder', {folderId: meta.folderId});
      }
    } catch (err) {
      console.error('media upload modal error', err);
    }
    dispatch(setUploadInProgress(false));
    setLoading(false);
  });
  return {
    isEditorProject,
    confirmFile: confirmFileMutation.mutateAsync,
    processingFile: confirmFileMutation.isLoading,
    setVideoMeta,
    pickerFile,
    pickerFileMeta,
    fileUploadProgress,
    isUploadInProgress,
    encryptionEnabled,
    mediaModalVisible,
    loading,
    videoMeta,
    uploadToCloud,
    dispatch,
    isVideoType: isVideo || isAudio,
    isImageType,
    fileType,
  };
}
